import { useMutation, useQuery } from "@apollo/client";
import TableContainer from "@mui/material/TableContainer";
import {
  Box,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
} from "@mui/material";
import { DELETE_STAGE, GET_ALL_STAGES } from "./graphql";
import { LinkButton } from "../../components/LinkButton";
import { PageContainer } from "../../components/PageContainer";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { UPDATE_STAGE } from "../stage/graphql";
import React from "react";

type StageRow = {
  id: string;
  name: string;
  order: number;
};

const normalizeOrder = (value?: number | null): number => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return 0;
  }

  return Math.max(0, value);
};

const reorderStages = (
  items: StageRow[],
  draggedStageId: string,
  targetStageId: string
): StageRow[] => {
  const fromIndex = items.findIndex((item) => item.id === draggedStageId);
  const toIndex = items.findIndex((item) => item.id === targetStageId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
    return items;
  }

  const reordered = [...items];
  const [draggedItem] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, draggedItem);

  return reordered.map((item, index) => ({
    ...item,
    order: index,
  }));
};

const Stages = () => {
  const { data, loading, refetch } = useQuery(GET_ALL_STAGES, {
    fetchPolicy: "cache-first",
  });
  const [updateStage] = useMutation(UPDATE_STAGE);
  const [rows, setRows] = React.useState<StageRow[]>([]);
  const [draggingStageId, setDraggingStageId] = React.useState<string | null>(
    null
  );
  const [dropTargetStageId, setDropTargetStageId] = React.useState<
    string | null
  >(null);
  const [isSavingOrder, setIsSavingOrder] = React.useState(false);
  const [deleteStage] = useMutation(DELETE_STAGE, {
    onCompleted: () => {
      toast("Stage deleted");
      refetch();
    },
  });

  React.useEffect(() => {
    const nextRows =
      data?.stages.map((stage) => ({
        id: stage.id,
        name: stage.name,
        order: normalizeOrder(stage.order),
      })) ?? [];

    setRows(nextRows);
  }, [data]);

  const originalOrderById = React.useMemo(() => {
    const map = new Map<string, number>();
    (data?.stages ?? []).forEach((stage) => {
      map.set(stage.id, normalizeOrder(stage.order));
    });
    return map;
  }, [data]);

  const hasOrderChanges = React.useMemo(() => {
    return rows.some((row) => {
      return originalOrderById.get(row.id) !== row.order;
    });
  }, [originalOrderById, rows]);

  const handleDelete = (stageId: string) => {
    confirmAlert({
      title: "Delete this Stage?",
      message: `Are you sure? this can't be undone. NOTE: all bookings will be deleted!`,
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteStage({ variables: { stageId: stageId } }),
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const handleSaveOrder = async () => {
    if (!hasOrderChanges || isSavingOrder) return;

    setIsSavingOrder(true);
    let updatedCount = 0;
    let firstError: string | null = null;

    for (const row of rows) {
      const previousOrder = originalOrderById.get(row.id);
      if (previousOrder === row.order) continue;

      try {
        await updateStage({
          variables: {
            id: row.id,
            name: row.name,
            order: normalizeOrder(row.order),
          },
        });
        updatedCount += 1;
      } catch (err) {
        firstError =
          firstError ??
          (err instanceof Error ? err.message : "Failed to update stage");
      }
    }

    if (updatedCount > 0) {
      toast(
        updatedCount === 1
          ? "Stage order updated"
          : `${updatedCount} stage orders updated`
      );
      await refetch();
    }

    if (firstError) {
      toast.error(`Some stage orders could not be saved: ${firstError}`);
    }

    setIsSavingOrder(false);
  };

  if (loading) return null;

  return (
    <PageContainer title="Stages">
      <LinkButton
        path="/stage/create"
        text="Create new Stage"
        variant={"contained"}
      />
      <Box
        sx={{
          mt: 2,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <Box component="span" sx={{ color: "text.secondary", fontSize: 14 }}>
          Drag rows to reorder stages, then save changes.
        </Box>
        <Button
          variant="contained"
          onClick={handleSaveOrder}
          disabled={!hasOrderChanges || isSavingOrder}
        >
          {isSavingOrder ? "Saving..." : "Save order"}
        </Button>
      </Box>
      <TableContainer component={Paper} style={{ marginTop: 16 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left" sx={{ width: 80 }}>
                drag
              </TableCell>
              <TableCell align="left">name</TableCell>
              <TableCell align="left">order</TableCell>
              <TableCell align="center">actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.id}
                draggable
                onDragStart={() => {
                  setDraggingStageId(row.id);
                }}
                onDragEnd={() => {
                  setDraggingStageId(null);
                  setDropTargetStageId(null);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  if (dropTargetStageId !== row.id) {
                    setDropTargetStageId(row.id);
                  }
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  if (!draggingStageId) return;
                  setRows((current) =>
                    reorderStages(current, draggingStageId, row.id)
                  );
                  setDraggingStageId(null);
                  setDropTargetStageId(null);
                }}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                style={{
                  opacity: draggingStageId === row.id ? 0.55 : 1,
                  backgroundColor:
                    dropTargetStageId === row.id
                      ? "rgba(25, 118, 210, 0.08)"
                      : undefined,
                  cursor: "move",
                }}
              >
                <TableCell align="left">::</TableCell>
                <TableCell align="left">{row.name}</TableCell>
                <TableCell align="left">{row.order}</TableCell>
                <TableCell align="center">
                  <LinkButton path={`/stage/${row.id}`} text="View" />
                  <Button
                    title="Delete"
                    variant="contained"
                    color="error"
                    onClick={() => handleDelete(row.id)}
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default Stages;
